import React, { FC, useEffect, useState } from 'react';
import cn from 'classnames';
import { onFieldOpened, onFieldClosed } from 'app/events/shortcuts';
import {
  autoUpdateSelector,
  darkenPageSelector,
  openedOverlaySelector,
  optionsSelector,
  themeSelector,
} from 'app/store/selectors';
import { useAppSelector } from 'app/hooks/appHooks';
import { emitSearchMaskReadyEvent } from 'app/events';
import { DateRangeWrapper } from 'app/components/date/DateRangeWrapper/DateRangeWrapper';
import { SearchButton } from 'app/components/ui/SearchButton';
import { TripLocationWrapper } from 'app/containers/TripLocationWrapper';
import TripProductPicker from 'app/containers/TripProductPicker';
import { TripModePicker } from 'app/containers/TripModePicker/TripModePicker';
import { Head } from 'app/containers/Head';
import * as styles from './SearchMask.scss';
import { useDataDog } from 'app/hooks/useDataDog';
import { getEventEmitter } from '@flixbus/transclusion-eventing';
import { TripLocationMobilePanel } from 'app/components/tripLocation/TripLocationMobilePanel/TripLocationMobilePanel';
import { ProductMobilePanel } from 'app/components/product/ProductMobilePanel/ProductMobilePanel';
import { SearchMaskMountOptions } from '@webc/meeseeks-ui-sdk';
import { themeFlix, themeKamil, themeNeptune, ThemeWrapper } from '@flixbus/honeycomb-react';

export const SearchMask: FC = () => {
  const { variant } = useAppSelector(optionsSelector);
  const dataDog = useDataDog();

  const isPanelOpened = !!useAppSelector(openedOverlaySelector);

  const [isOnlyElement, setIsOnlyElement] = useState(false);
  const [localVariant, setLocalVariant] = useState(variant);
  const [localTheme, setLocalTheme] = useState<SearchMaskMountOptions['theme']>('flix');

  const [pageDarkened, setPageDarkened] = useState(false);

  const isDarkenPageEnabled = useAppSelector(darkenPageSelector);
  const theme = useAppSelector(themeSelector);

  const isAutoUpdate = useAppSelector(autoUpdateSelector);

  const themes = { flix: themeFlix, kamil: themeKamil, neptune: themeNeptune };

  const onPageLoad = () => {
    const mainElementExist =
      !!document.querySelectorAll('[role="main"]').length ||
      !!document.getElementsByTagName('main').length;
    setIsOnlyElement(!mainElementExist);
  };

  useEffect(() => {
    if (document.readyState === 'complete') {
      onPageLoad();
    } else {
      window.addEventListener('load', onPageLoad, false);
      return () => window.removeEventListener('load', onPageLoad);
    }
  }, []);

  useEffect(() => {
    setLocalVariant(variant);
  }, [variant]);

  useEffect(() => {
    setLocalTheme(theme);
  }, [theme]);

  useEffect(() => {
    const listener = function (extraContext: Record<string, unknown>) {
      // @ts-expect-error ignore
      dataDog.addAction((this.event as string).replace('search.search-mask.', ''), extraContext);
    };
    getEventEmitter().on('search.search-mask.*', listener);
    return () => {
      getEventEmitter().off('search.search-mask.*', listener);
    };
  });
  useEffect(() => {
    emitSearchMaskReadyEvent();
    onFieldOpened(() => {
      if (isDarkenPageEnabled) {
        setPageDarkened(true);
      }
    });
    onFieldClosed(() => {
      setPageDarkened(false);
    });
  }, [isDarkenPageEnabled]);

  return (
    <div role={isOnlyElement ? 'main' : undefined}>
      <ThemeWrapper themes={themes} theme={localTheme}>
        <Head />
        <div
          aria-hidden={isPanelOpened}
          className={cn(styles.searchMask, {
            [styles.frontpage]: localVariant === 'frontpage',
            [styles.compact]: localVariant === 'compact',
            [styles.fullWidth]: localVariant === 'fullWidth',
            [styles.pageDarkened]: Boolean(pageDarkened),
          })}
        >
          <div className={styles.searchMaskWrapper}>
            <div className={styles.tripModeWrapper}>
              <TripModePicker />
            </div>

            <div className={styles.tripLocationWrapper}>
              <TripLocationWrapper variant={variant} />
            </div>

            <div className={styles.dateRangeWrapper}>
              <DateRangeWrapper />
            </div>

            <div className={styles.tripProductWrapper}>
              <TripProductPicker />
            </div>

            {!isAutoUpdate && (
              <div className={styles.searchButtonWrapper}>
                <SearchButton />
              </div>
            )}
          </div>
        </div>
        <TripLocationMobilePanel />
        <ProductMobilePanel />
      </ThemeWrapper>
    </div>
  );
};
